import React, { useState, createContext, useCallback, useRef, useMemo } from 'react';
import { DEFAULT_COPILOT, toDefaultAwareAgentOption } from '../constants/defaultCopilot';

const context = {};

export const TemporaryChatContext = createContext(context);

export const TemporaryChatContextProvider = props => {
  // Icon/label are hydrated from the agents API in PolyChatHeader — avoid a stale hardcoded icon.
  const [selectedAgent, setSelectedAgent] = useState(() => {
    const saved = localStorage.getItem('agent');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Keep team icons (not in agents list); agent icons refresh from API on chat load.
        if (parsed?.mode === 'team') return parsed;
        // Always brand the site default Jose (and migrate legacy Poly / Ferdy / FLO).
        const legacyIds = new Set([
          DEFAULT_COPILOT.value,
          '900468f9-9a77-482a-9299-87dd9f2c04c5',
          'ferdy',
          '2446bcb2-8e0e-41a8-aa65-e58f09b7b776',
        ]);
        const legacyNames = new Set(['poly', 'ferdy', 'flo', 'flo chat', 'jose', 'isaac t']);
        if (
          legacyIds.has(String(parsed?.value)) ||
          legacyNames.has((parsed?.label || '').toLowerCase())
        ) {
          return { ...DEFAULT_COPILOT };
        }
        return { ...parsed, icon: null };
      } catch {
        /* fall through */
      }
    }
    return { ...DEFAULT_COPILOT };
  });

  const [chatMessages, setChatmessages] = useState([]);
  const [workflowChatMessages, setWorkflowChatmessages] = useState([]);
  const [toolImages, setToolImages] = useState([]);
  const [chatLoaderMessages, setChatLoaderMessages] = useState({
    newChunk: [{ tool_name: 'thinking...' }],
  });
  const [webSearch, setWebSearch] = useState(false);
  const [tempText, setTempText] = useState('');
  const [sessionID, setSessionID] = useState(null);
  const [sessionAgent, setSessionAgent] = useState({ ...DEFAULT_COPILOT });
  const isActiveRef = useRef(true);

  const fakeAiLoader = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      const updatedLast = { ...last, isLoading: true, isStreaming: true };
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const fakeAiLoaderClear = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      const updatedLast = { ...last, isLoading: false, isStreaming: false };
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const updateUserChat = useCallback(message => {
    const userMessage = {
      human: message,
      ai: '',
      isStreaming: true,
      source: [],
      intermediate_steps: [
        {
          tool_name: 'thinking...',
          content: message,
          isLoading: true,
        },
      ],
    };
    setChatmessages(prev => [...prev, userMessage]);
  }, []);

  const updateBotChat = useCallback(newChunk => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      const updatedLast = { ...last, ai: (last.ai || '') + newChunk, isLoading: false, isStreaming: true };
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const finishStream = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      const updatedLast = { ...last, isStreaming: false, isLoading: false };
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const updateDelegateBotChat = useCallback(delegateAgent => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length];
      const updatedLast = { ...last, delegate_agent: delegateAgent, ai: ' ', isLoading: false };
      return [...prev, updatedLast];
    });
  }, []);

  const updateStepsChat = useCallback(newChunk => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      const existingSteps = Array.isArray(last.intermediate_steps) ? last.intermediate_steps : [];

      const silencedSteps = existingSteps.map(step => ({
        ...step,
        isLoading: false,
      }));

      const updatedSteps = [...silencedSteps, { ...newChunk, isLoading: true }];

      const updatedLast = {
        ...last,
        intermediate_steps: updatedSteps,
        isLoading: false,
      };

      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const updateStepsResultsChat = useCallback(newChunk => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      const steps = Array.isArray(last.intermediate_steps) ? last.intermediate_steps : [];

      const stepExists = steps.some(step => step.tool_call_id === newChunk?.tool_call_id);

      const updatedSteps = stepExists
        ? steps.map(step => (step.tool_call_id === newChunk?.tool_call_id ? newChunk : step))
        : [...steps, newChunk];

      const updatedLast = {
        ...last,
        intermediate_steps: updatedSteps,
        isLoading: false,
      };

      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const updateAllStepsLoading = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];

      if (!last?.intermediate_steps || !Array.isArray(last?.intermediate_steps)) {
        return prev;
      }

      const finalizedSteps = last?.intermediate_steps?.map(step => ({
        ...step,
        isLoading: false,
      }));

      const updatedLast = {
        ...last,
        intermediate_steps: finalizedSteps,
        isLoading: false,
      };

      return [...prev.slice(0, -1), updatedLast];
    });
  }, [setChatmessages]);

  const updateThinkingStepsLoading = useCallback(() => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[0];

      const updatedLast = {
        ...last,
        isLoading: false,
      };

      return [updatedLast];
    });
  }, [setChatmessages]);

  const updateWorkflowUserChat = useCallback(message => {
    const userMessage = { human: message, ai: '', source: [] };
    setWorkflowChatmessages(prev => [...prev, userMessage]);
  }, []);

  const updateWorkflowBotChat = useCallback(newChunk => {
    setWorkflowChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      const updatedLast = { ...last, ai: (last.ai || '') + newChunk, isLoading: false };
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const updateToolImages = useCallback(newChunk => {
    setChatmessages(prev => {
      if (prev.length === 0) return prev;

      const last = prev[prev.length - 1];
      const updatedLast = { ...last, images: newChunk?.search_results, isLoading: false };
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const updateChatLoaderMessage = useCallback(newChunk => {
    setChatLoaderMessages(prev => {
      const updatedLast = { newChunk, isLoading: false };
      return updatedLast;
    });
  }, []);

  const updateChatSource = useCallback(newSources => {
    setChatmessages(prev => {
      const last = prev[prev.length - 1];

      const websearchSources = newSources.filter(src => src.type === 'google_search');
      const fileSources = newSources.filter(src => src.type === 'file');

      const updatedLast = {
        ...last,
        source: websearchSources.length > 0 ? websearchSources : [],
        files: fileSources.length > 0 ? fileSources : [],
      };
      return [...prev.slice(0, -1), updatedLast];
    });
  }, []);

  const updateWebSearch = useCallback(() => {
    setWebSearch(prev => !prev);
  }, []);

  const updateAgent = useCallback(agent => {
    const branded = toDefaultAwareAgentOption(agent) || agent;
    localStorage.setItem('agent', JSON.stringify(branded));
    setSelectedAgent(branded);
  }, []);

  const toggleRef = useCallback(value => {
    isActiveRef.current = value;
  }, []);

  const contextValue = useMemo(
    () => ({
      chatMessages,
      workflowChatMessages,
      chatLoaderMessages,
      updateWorkflowBotChat,
      updateWorkflowUserChat,
      updateStepsChat,
      updateAllStepsLoading,
      updateStepsResultsChat,
      setSessionID,
      sessionID,
      setChatmessages,
      toolImages,
      setToolImages,
      updateToolImages,
      updateUserChat,
      updateBotChat,
      finishStream,
      updateChatLoaderMessage,
      updateChatSource,
      fakeAiLoader,
      fakeAiLoaderClear,
      webSearch,
      updateWebSearch,
      setTempText,
      tempText,
      selectedAgent,
      setSelectedAgent,
      updateAgent,
      setSessionAgent,
      sessionAgent,
      toggleRef,
      isActiveRef,
      updateDelegateBotChat,
      updateThinkingStepsLoading,
    }),
    [
      chatMessages,
      workflowChatMessages,
      chatLoaderMessages,
      updateWorkflowBotChat,
      updateWorkflowUserChat,
      updateStepsChat,
      updateAllStepsLoading,
      updateStepsResultsChat,
      sessionID,
      toolImages,
      updateToolImages,
      updateUserChat,
      updateBotChat,
      finishStream,
      updateChatLoaderMessage,
      updateChatSource,
      fakeAiLoader,
      fakeAiLoaderClear,
      webSearch,
      updateWebSearch,
      tempText,
      selectedAgent,
      updateAgent,
      sessionAgent,
      toggleRef,
      updateDelegateBotChat,
      updateThinkingStepsLoading,
    ],
  );

  return <TemporaryChatContext.Provider value={contextValue}>{props.children}</TemporaryChatContext.Provider>;
};
